import type {ReactElement} from 'react';
import {useState} from 'react';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {InputField} from '@Components/input-field-v2';
import {InputFieldSize, InputFieldType} from '@Components/input-field-v2/input-field.types';
import {Text, TextSize} from '@Components/text';
import type {ButtonTextPickerProps} from './button-text-picker.types';
import styles from './button-text-picker.module.scss';

const PILL_BASE_CLASSES = 'corner-radius-full border-s-standard flex-row-align-center spacing-p-l-3 spacing-p-r-3';
const DEFAULT_MAX_LENGTH = 50;

export function ButtonTextPicker({value, onChange, options, label, maxLength = DEFAULT_MAX_LENGTH}: ButtonTextPickerProps): ReactElement {
  const [isTypingCustomText, setIsTypingCustomText] = useState<boolean>(false);
  const [customText, setCustomText] = useState<string>('');

  const effectiveValue = value === '' ? options[0] : value;
  const storedCustomValue = value !== '' && !options.includes(value) ? value : '';

  const onSubmitCustomText = (inputVal?: string): void => {
    const trimmed = (inputVal ?? customText).trim();
    if (trimmed === '') {
      setIsTypingCustomText(false);
      return;
    }
    onChange(trimmed);
    setCustomText('');
    setIsTypingCustomText(false);
  };

  const getPill = (pillLabel: string, isSelected: boolean, onClick: () => void): ReactElement => {
    return (
      <ClickableDiv key={pillLabel} className={`${styles.pill} ${isSelected ? styles.selected : ''} ${PILL_BASE_CLASSES}`} onClick={onClick}>
        <Text val={pillLabel} size={TextSize.MD} bold />
      </ClickableDiv>
    );
  };

  const getPresetPills = (): ReactElement[] => {
    return options.map((option) => {
      return getPill(option, option === effectiveValue, () => {
        onChange(option);
      });
    });
  };

  const getCustomPill = (): ReactElement | null => {
    if (storedCustomValue === '') {
      return null;
    }
    return getPill(storedCustomValue, true, () => {
      onChange(storedCustomValue);
    });
  };

  const getCustomTextControl = (): ReactElement => {
    if (isTypingCustomText) {
      return (
        <InputField
          className={styles.customInput}
          inputClassName="corner-radius-8"
          input={customText}
          size={InputFieldSize.XSMALL}
          inputType={InputFieldType.TEXT}
          maxLength={maxLength}
          enforceMaxLength
          isValueBlank
          onInputChange={(val): void => {
            setCustomText(val.toString());
          }}
          onInputSubmit={onSubmitCustomText}
          onInputBlur={(val): void => {
            onSubmitCustomText(val.toString());
          }}
          autoFocus
        />
      );
    }

    return (
      <ClickableDiv
        className={`${styles.pill} ${PILL_BASE_CLASSES}`}
        onClick={(): void => {
          setIsTypingCustomText(true);
        }}
      >
        <Icon className={`${styles.customPillIcon} spacing-m-r-1`} icon="icon-plus" size={IconSize.SIZE_ICON_16} />
        <Text val={window.i18next.t('pmwjs_event_ticketing_custom_text')} size={TextSize.MD} bold />
      </ClickableDiv>
    );
  };

  return (
    <div className="flex-column-align-start _full-width">
      <Text className="spacing-m-b-2" val={label} size={TextSize.SM} bold />
      <div className="flexbox flex-wrap gap-2 _full-width">
        {getPresetPills()}
        {getCustomPill()}
        {getCustomTextControl()}
      </div>
    </div>
  );
}
